import { Box, Grid, Stack, Typography } from "@mui/material";
import ManagementHeader from "../ManagementsUpdateHeader/ManagementsUpdateHeader";
import { useTranslation } from "react-i18next";
import CupsCifSelector from "../../CupsCifSelector/CupsCifSelector";
import {
  CupsIbanValidator,
  DomiciliationBillingInfo,
  DomiciliationFileArray,
  ManagementUpdateDomiciliationData,
  UpdatePaymentMethodRequestBody,
} from "../../../core/models/managementsUpdate/managementUpdateDomiciliation/managementUpdateDomiciliation.model";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import theme from "../../../muiTheme";
import "./ManagementUpdateDomiciliation.scss";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import { useSearchParams } from "react-router-dom";
import ManagementsRequestState from "../../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  getPaymentMethod,
  postImportDocument,
  updatePaymentMethod,
} from "../../../core/services/managements/managements.service";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import {
  MANAGEMENTS_STATE_BANK_ERROR,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_RESOLVED,
  MANAGEMENTS_SUBTYPE_UPDATE_DOMICILIATION,
} from "../../../core/constants/constants";
import {
  ManagementRequestInfo,
  RequestStateDescriptionData,
} from "../../../core/models/managements/managements.model";
import {
  initialManagementRequestInfo,
  getManagementsRequestTypeData,
  initialRequestStateData,
  formatOwnerDataCup,
} from "../../../utils/components/managements/managements.util";
import { formatDate } from "../../../utils/dateUtils/formatDate.util";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";
import ManagementsCancelProcess from "../../Managements/ManagementsCancelProcess/ManagementsCancelProcess";
import FormIban from "../../UI/FormIban/FormIban";

const ManagementUpdateDomiciliation = () => {
  const { t } = useTranslation();
  const {
    ownerData,
    userData,
    cifCupsState: { listCupsCifFiltered },
    isAgent,
  } = useContext(TotalEnergiesContext);

  const [searchParms] = useSearchParams();
  const idCups = searchParms.get("idCups") || "";
  const [step, setStep] = useState<number>(idCups.length ? 0 : -1);
  const [updateDomiciliationData, setUpdateDomiciliationData] =
    useState<ManagementUpdateDomiciliationData>({
      idClient: "",
      isAdministrator: "",
      cups: [],
      contacts: [],
    });
  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>(initialRequestStateData);
  const [managementRequestInfo, setManagementRequestInfo] = useState<
    ManagementRequestInfo[]
  >([initialManagementRequestInfo]);
  const [documentImportErrorReferences, setDocumentImportErrorReferences] =
    useState<string[]>([]);
  const [selectedCups, setSelectedCups] = useState<ManagementUpdateCup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPaymentMethodLoading, setIsPaymentMethodLoading] =
    useState<boolean>(false);
  const [pendingOcrRequests, setPendingOcrRequests] = useState<number>(0);
  const ownerDataCup =
    ownerData?.cups.find(
      ({ idCups: ownerDataIdCups }) => ownerDataIdCups.toString() === idCups,
    ) || initialOwnerDataCup;
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const [billingInformation, setBillingInformation] = useState<
    DomiciliationBillingInfo[]
  >([]);

  const [requestBody, setRequestBody] = useState<
    UpdatePaymentMethodRequestBody[]
  >([]);

  const [fileArray, setFileArray] = useState<DomiciliationFileArray[]>([]);
  const [currentIbanChangeFlag, setCurrentIbanChangeFlag] =
    useState<boolean>(true);
  const [ibanChangedMap, setIbanChangedMap] = useState<Record<string, boolean>>(
    {},
  );

  const handleIbanSelectionChange = useCallback(
    (cupsCode: string, isChangedFromActive: boolean) => {
      setIbanChangedMap((prev) => {
        const updated = { ...prev, [cupsCode]: isChangedFromActive };
        const anyChanged = Object.values(updated).some((changed) => changed);
        setCurrentIbanChangeFlag(!anyChanged);
        return updated;
      });
    },
    [],
  );

  useEffect(() => {
    const abortController = abortControllerRef.current;

    const fetchPaymentData = async () => {
      setIsPaymentMethodLoading(true);
      try {
        const listCupsCifFilteredUnique = listCupsCifFiltered.map(
          (element) => ({
            ...element,
            cups: element.cups.reduce(
              (
                accumulator: ManagementUpdateCup[],
                current: ManagementUpdateCup,
              ) => {
                if (
                  !accumulator.find((item) => item.cupsId === current.cupsId)
                ) {
                  accumulator.push(current);
                }
                return accumulator;
              },
              [],
            ),
          }),
        );

        const cupsList = listCupsCifFilteredUnique.flatMap((item) =>
          item.cups.map((cup) => cup.cupsCode),
        );

        const data = await getPaymentMethod(
          { cups: cupsList },
          userData?.token || "",
          abortController.signal,
        );

        setBillingInformation(data);
      } catch (err) {
        console.error("Error fetching payment data:", err);
      }
      setIsPaymentMethodLoading(false);
    };

    fetchPaymentData();

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    if (ownerData) {
      const { isAdministrator, idAdministrator, idClient } = ownerData;
      setUpdateDomiciliationData((prevUpdateDomiciliationData) => ({
        ...prevUpdateDomiciliationData,
        idClient: isAdministrator ? idAdministrator : idClient,
        isAdministrator: isAdministrator.toString(),
      }));
    }

    if (idCups.length && ownerData) {
      setUpdateDomiciliationData((prevUpdateDomiciliationData) => ({
        ...prevUpdateDomiciliationData,
        cups: [formatOwnerDataCup(ownerDataCup)],
      }));
    }
  }, [idCups, ownerData, ownerDataCup]);

  const handleSubmit = async () => {
    if (userData) {
      setStep(1);
      setIsLoading(true);
      setDocumentImportErrorReferences([]);
      try {
        const response = await updatePaymentMethod(
          { cups: requestBody },
          userData.token,
        );

        const failedImportReferences: string[] = [];
        const requestInfo = await Promise.all(
          response.map(async (res) => {
            const foundFile = fileArray.find((item) => item.cups === res.Cups);
            if (foundFile) {
              try {
                await postImportDocument(
                  res.IdCallCenter,
                  res.IdCarpetaIncidencias,
                  foundFile.file,
                  userData.token,
                );
              } catch {
                failedImportReferences.push(res.IdCallCenter.toString());
              }
            }

            const address = ownerData?.cups.find(
              (data) => data.codeCups === res.Cups,
            )?.address;
            return {
              registrarionDate: formatDate(new Date()),
              refferenceNumber: res.IdCallCenter.toString(),
              cupsInfo: {
                cupsCode: res.Cups,
                address: address ?? "",
                environment: res.EntornoBD,
              },
            };
          }),
        );

        setDocumentImportErrorReferences(failedImportReferences);
        setRequestState(MANAGEMENTS_STATE_RESOLVED);
        setRequestStateData(
          getManagementsRequestTypeData(
            MANAGEMENTS_SUBTYPE_UPDATE_DOMICILIATION,
            MANAGEMENTS_STATE_RESOLVED,
            isAgent ?? false,
          ),
        );
        setManagementRequestInfo(requestInfo);
        setIsLoading(false);
      } catch (error) {
        const errorMessage = (error as any)?.response?.data?.message as
          | string
          | undefined;
        const bankError = !!errorMessage?.includes(
          "no corresponde a ningún banco registrado",
        );

        setRequestState(MANAGEMENTS_STATE_ERROR);
        setRequestStateData(
          getManagementsRequestTypeData(
            MANAGEMENTS_SUBTYPE_UPDATE_DOMICILIATION,
            bankError ? MANAGEMENTS_STATE_BANK_ERROR : MANAGEMENTS_STATE_ERROR,
            isAgent ?? false,
          ),
        );
        setIsLoading(false);
      }
    }
  };

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const [ibanValidations, setIbanValidations] = useState<CupsIbanValidator>({});

  useEffect(() => {
    const newValidation = selectedCups.reduce((acc, item) => {
      acc[item.cupsCode] = { validIban: true, validDocument: true };
      return acc;
    }, {} as CupsIbanValidator);
    setIbanValidations(newValidation);
  }, [selectedCups]);

  const isButtonDisabled =
    !Object.values(ibanValidations).every(
      (item) => item.validIban && item.validDocument,
    ) ||
    currentIbanChangeFlag ||
    pendingOcrRequests > 0;

  return (
    <Box component="section" className="management-update-domiciliation">
      <ManagementHeader
        title={"Cambio de domiciliación bancaria"}
        step={step}
        cupsSelected={updateDomiciliationData.cups}
        ownerDataCup={ownerDataCup}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="center"
        paddingTop={5}
        paddingBottom={10}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        {step === -1 && (
          <CupsCifSelector
            isLoading={isPaymentMethodLoading}
            withContainer
            setUpdateCups={(selectedCups) => {
              setUpdateDomiciliationData((prevUpdateDomiciliationData) => ({
                ...prevUpdateDomiciliationData,
                cups: selectedCups,
              }));
              setStep(0);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
            selectedCups={selectedCups}
            setSelectedCups={setSelectedCups}
            title={"Gestiones.¿Dónde quieres aplicar el cambio?"}
            description={"Gestiones.El cambio se solicitará"}
            billingInformation={billingInformation}
          />
        )}
        {step === 0 && (
          <Grid container justifyContent="center" paddingX={2}>
            <Grid item md={8} lg={7} flex={1}>
              <Stack
                component="div"
                className="management-update-domiciliation__content-container"
                spacing={4}
              >
                <Stack component="div" spacing={2}>
                  <Typography variant="h3" component="p">
                    {t("Gestiones.Seleccione el IBAN")}
                  </Typography>
                  <Typography variant="body2" component="p">
                    {t(
                      "Gestiones.Seleccione el IBAN en el que quiere domiciliar los pagos",
                    )}
                  </Typography>
                </Stack>
                <Stack component="div" spacing={2}>
                  {selectedCups &&
                    selectedCups.map((cup, index) => (
                      <FormIban
                        setFileArray={setFileArray}
                        fileArray={fileArray}
                        key={index}
                        cup={cup}
                        billingInfo={billingInformation.find(
                          (item) => item.Cups === cup.cupsCode,
                        )}
                        isAgent={isAgent}
                        setIbanValidations={setIbanValidations}
                        setRequestBody={setRequestBody}
                        onIbanSelectionChange={handleIbanSelectionChange}
                        onOcrRequestStart={() =>
                          setPendingOcrRequests((count) => count + 1)
                        }
                        onOcrRequestEnd={() =>
                          setPendingOcrRequests((count) =>
                            Math.max(0, count - 1),
                          )
                        }
                      />
                    ))}
                </Stack>

                <Box
                  component="div"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  className="management-update-domiciliation__actions"
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                    flexWrap: "wrap",
                  }}
                >
                  <ButtonElement
                    onClick={() => {
                      handleSubmit();
                      window.scrollTo({ top: 0, behavior: "auto" });
                    }}
                    disabled={isButtonDisabled}
                  >
                    {t("Gestiones.Solicitar cambio de domiciliación")}
                  </ButtonElement>
                  <ButtonElement type="button" onClick={openModal} secondary>
                    {t("Cancelar")}
                  </ButtonElement>
                </Box>
              </Stack>
              <ManagementsCancelProcess
                open={isModalOpen}
                handleClose={closeModal}
              />
            </Grid>
          </Grid>
        )}
        {step === 1 && (
          <Box width={"100%"}>
            {managementRequestInfo.map((request) => (
              <ManagementsRequestState
                key={request.refferenceNumber}
                showManagementRequestInfo
                isLoading={isLoading}
                state={requestState}
                managementRequestInfo={request}
                title={requestStateData.title}
                description={requestStateData.description}
                descriptionExtra={requestStateData.descriptionExtra}
                cupsInfo={request.cupsInfo}
                documentImportError={documentImportErrorReferences.includes(
                  request.refferenceNumber,
                )}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ManagementUpdateDomiciliation;
